// ── PositionTracker orders-table sort ────────────────────────────
//
// Extracted from index.tsx so the comparator is a pure, testable unit.
// The orders table interleaves Kalshi and Polymarket rows; sorting has
// to work off the ONE key both kinds carry (the order id) plus the
// stabilized live metrics snapshot (see stableOrderSortKeys in
// index.tsx, refreshed on the ORDER_SORT_STABILIZE_MS cadence).

import type { KalshiOrder } from '../../types/kalshi';
import type { PolyRelayOpenOrder } from '../../lib/polymarket/relay';
import type { Sport } from '../ValueDashboard/types';

export type SortedOrderRow =
  | {
      kind: 'kalshi';
      sport: Sport | null;
      sortStart: number;
      sortTicker: string;
      order: KalshiOrder;
    }
  | {
      kind: 'poly';
      sport: Sport | null;
      sortStart: number;
      sortTicker: string;
      order: PolyRelayOpenOrder;
    };

/** Stabilized live sort keys, one entry per order id (Kalshi AND poly).
 *  COMPARABILITY CAVEAT: Kalshi queue is the venue's authoritative own-slot
 *  FIFO position (atLevel + better); poly queue is the pessimistic
 *  whole-level-ahead upper bound from polyOrderQueueFromBook (the CLOB has
 *  no per-order FIFO slot). Sorting mixes the two on purpose — they are the
 *  same numbers the queue cells display, so the row order always matches
 *  what the trader reads. */
export interface OrderSortLiveKeys {
  queue: number | null;
  velocity: number | null;
}

export function compareDefaultSort(
  a: { sortStart: number; sortTicker: string },
  b: { sortStart: number; sortTicker: string },
): number {
  if (a.sortStart !== b.sortStart) return a.sortStart - b.sortStart;
  return a.sortTicker.localeCompare(b.sortTicker);
}

/**
 * Event-start sort key for a Polymarket row (position OR order).
 *
 * Poly rows are keyed by the market conditionId (positions) or a CLOB asset
 * token id (orders); neither resolves in the event-start cache, so the rows
 * used to fall to the unknown-time tail (+Infinity) even when the game's start
 * was known. The row's CACHED description carries the slug `PM-…` ticker, and
 * that DOES resolve — to the SAME start the Kalshi side of the game resolves
 * to (the start cache keys by canonicalGameKey across venues), so a game's
 * Kalshi and Poly rows sort together.
 *
 * `resolveStartMs` is the caller's `sortStartMs` — the store's
 * getEventStartTimeMsByTicker wrapped to sentinel +Infinity on a miss. Pure
 * store/cache read, NO network. An unresolved description (empty pmTicker) or
 * a resolved market with no start keeps the row at the tail.
 */
export function polySortStartMs(
  pmTicker: string | null | undefined,
  resolveStartMs: (ticker: string) => number,
): number {
  if (!pmTicker) return Number.POSITIVE_INFINITY;
  return resolveStartMs(pmTicker);
}

/** The one id every row has: Kalshi orderId, poly CLOB id (id / orderID /
 *  orderId — the same derivation the row renderers use). Doubles as the key
 *  into the stabilized sort-keys map. */
export function orderRowId(row: SortedOrderRow): string {
  if (row.kind === 'kalshi') return row.order.orderId;
  const o = row.order;
  return String(o.id ?? o.orderID ?? o.orderId ?? '');
}

/** Default sort with a final order-id tiebreak for BOTH kinds. Same-token
 *  poly ladder orders tie completely in compareDefaultSort (sortTicker =
 *  assetId, sortStart = Infinity) and previously followed raw CLOB response
 *  order — reshuffling every poll. */
export function compareOrderRowsDefault(a: SortedOrderRow, b: SortedOrderRow): number {
  const d = compareDefaultSort(a, b);
  if (d !== 0) return d;
  return orderRowId(a).localeCompare(orderRowId(b));
}

/** Remaining contracts, venue-native: Kalshi remainingCount; poly
 *  original_size − size_matched (CLOB fields arrive number-or-string). */
export function orderRowSize(row: SortedOrderRow): number {
  if (row.kind === 'kalshi') return row.order.remainingCount;
  const original =
    typeof row.order.original_size === 'number'
      ? row.order.original_size
      : Number(row.order.original_size ?? 0);
  const matched =
    typeof row.order.size_matched === 'number'
      ? row.order.size_matched
      : Number(row.order.size_matched ?? 0);
  return Math.max(0, original - matched);
}

/** Comparator for the non-default sorts. `liveKeys` is the stabilized
 *  queue/velocity snapshot keyed by orderRowId — Kalshi AND poly ids (poly
 *  rows used to look up nothing and sat in a permanent null tail while
 *  their queue cells displayed a real number). Nulls (sells, dead book,
 *  unresolved market, no velocity source) keep the tail behavior in both
 *  directions; every tie lands on the id-stable default. */
export function makeOrderRowComparator(
  key: 'size' | 'queue' | 'velocity',
  dir: 1 | -1,
  liveKeys: ReadonlyMap<string, OrderSortLiveKeys>,
): (a: SortedOrderRow, b: SortedOrderRow) => number {
  const queueOf = (row: SortedOrderRow): number | null =>
    liveKeys.get(orderRowId(row))?.queue ?? null;
  const velocityOf = (row: SortedOrderRow): number | null =>
    liveKeys.get(orderRowId(row))?.velocity ?? null;
  return (a, b) => {
    if (key === 'size') {
      const sizeDelta = orderRowSize(a) - orderRowSize(b);
      if (sizeDelta !== 0) return sizeDelta * dir;
      return compareOrderRowsDefault(a, b);
    }
    if (key === 'velocity') {
      const va = velocityOf(a);
      const vb = velocityOf(b);
      if (va == null && vb == null) return compareOrderRowsDefault(a, b);
      if (va == null) return 1;
      if (vb == null) return -1;
      const velocityDelta = va - vb;
      if (velocityDelta !== 0) return velocityDelta * dir;
      return compareOrderRowsDefault(a, b);
    }
    const qa = queueOf(a);
    const qb = queueOf(b);
    if (qa == null && qb == null) return compareOrderRowsDefault(a, b);
    if (qa == null) return 1;
    if (qb == null) return -1;
    const queueDelta = qa - qb;
    if (queueDelta !== 0) return queueDelta * dir;
    return compareOrderRowsDefault(a, b);
  };
}
